import React, { useState, useRef } from 'react'
import {Upload, X} from 'lucide-react'
import { useFileUpload } from '../hooks/useFileUpload'
import { useProjects, Project } from '../hooks/useProjects'

interface BannerUploadModalProps {
  project: Project
  onClose: () => void
}

export function BannerUploadModal({ project, onClose }: BannerUploadModalProps) {
  const { uploadSingleFile, uploading } = useFileUpload()
  const { updateProject } = useProjects()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB')
        return
      }
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a banner image')
      return
    }

    try {
      const bannerUrl = await uploadSingleFile(selectedFile)
      await updateProject(project._id, {
        bannerImageUrl: bannerUrl
      })
      alert('Banner uploaded successfully!')
      onClose()
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload banner. Please try again.')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#252b3d] border border-white/10 rounded-2xl max-w-2xl w-full p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white">Upload Banner for {project.projectName}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Banner Upload */}
          <div>
            <label className="block text-gray-300 mb-2">Banner Image (1200x400px recommended)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {previewUrl ? (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg border border-white/10"
                />
                <button
                  onClick={() => {
                    setSelectedFile(null)
                    setPreviewUrl('')
                  }}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-[#3B82F6] transition-colors"
              >
                <Upload className="w-10 h-10 text-gray-500" />
                <span className="text-gray-400">Click to upload banner image</span>
                <span className="text-sm text-gray-500">Max size: 5MB</span>
              </button>
            )}
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Banner'}
          </button>
        </div>
      </div>
    </div>
  )
}
